import Accordion from "@/common/components/Accordion";
import Checkbox from "@/common/components/Checkbox";
import MaintenanceObjective from "@/common/components/MaintenanceObjective";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import ResumeItem from "@/common/components/ResumeItem";
import Skeleton from "@/common/components/Skeleton";
import { Pagination } from "@/common/components/Table/pagination";
import { formatDateTime } from "@/common/utils/formatters";
import { CameraOffIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { EmptyDataMessage } from "@/common/components/EmptyDataMessage";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { useFormContext } from "react-hook-form";
import { useInternalFieldArray } from "@/common/hooks/useInternalFieldArray";
import { useMaintenanceSolicitations } from "@/features/maintenanceSolicitations/hooks/useMaintenanceSolicitations";
import { MaintenanceSolicitationData } from "@/features/maintenanceSolicitations/types/maintenanceSolicitation";
import { MaintenanceWorkflowValidationSchema } from "@/features/maintenanceWorkflow/schemas/maintenanceWorkflow.schema";
import { solicitationKindTranslation } from "@/features/maintenanceSolicitations/constants/solicitationKind.constant";

const MaintenanceWorkflowSelectSolicitations = () => {
  const scrollListRef = useRef<HTMLDivElement>(null);

  const { filterParams } = useFilterParams();

  const [page, setPage] = useState<number | undefined>(1);

  const {
    data: solicitations,
    meta: solicitationsMeta,
    isPending: solicitationsIsPending,
    isFetching: solicitationsIsFetching,
  } = useMaintenanceSolicitations<MaintenanceSolicitationData[]>({
    filters: {
      page: page || 1,
      equipmentId: filterParams.equipment,
      status: 'pending',
    },
  });

  const formMethods = useFormContext<MaintenanceWorkflowValidationSchema>();

  const {
    fields: solicitationIds,
    append: appendSolicitationId,
    remove: removeSolicitationId,
  } = useInternalFieldArray({
    ...formMethods,
    name: 'solicitation_ids',
  });

  useEffect(() => {
    scrollListRef.current?.scrollTo({ top: 0 });
  }, [page]);

  return (
    <div>
      <div ref={scrollListRef} className="flex flex-col gap-2 max-h-[calc(100vh-33rem)] overflow-auto">
        {solicitationsIsFetching && new Array(10).fill(null).map((_, i) => (
          <div
            key={`solicitation-loading-${i}`}
            className="min-h-[68px] flex items-center gap-3 px-4 bg-white-100 rounded-sm"
          >
            <Skeleton className="size-6 rounded-lg bg-white-100" />

            <div className="flex flex-col gap-0.5">
              <Skeleton className="h-5 w-20 bg-white-100" />

              <Skeleton className="h-4 w-16 bg-white-100" />
            </div>
          </div>
        ))}

        {!solicitationsIsFetching && Number(solicitations.length) === 0 && (
          <EmptyDataMessage
            message="Não há solicitações para este equipamento"
            className="h-full py-0"
          />
        )}

        {!solicitationsIsFetching && solicitations.map((solicitation) => {
          const objectives = solicitation.objectives ?? [];
          const objectivesLabel = objectives
            .slice(0, 3)
            .map((objective) => `${objective.action.display_name} ${objective.part.display_name}`)
            .join(', ');
          const remainingObjectivesCount = objectives.length - 3;

          return (
          <Accordion
            key={solicitation.id}
            className="p-0"
            triggerClassName="px-4 py-4"
            bodyClassName="px-4 pb-4"
            defaultOpen={false}
            head={(
              <div className="flex items-center">
                <Checkbox
                  label=""
                  boxClassName="size-6"
                  checked={solicitationIds.includes(solicitation.id)}
                  onChange={(event) => {
                    if (event.currentTarget.checked) {
                      appendSolicitationId(solicitation.id);
                    } else {
                      removeSolicitationId(solicitationIds.indexOf(solicitation.id));
                    }
                  }}
                />

                <div className="flex flex-col items-start">
                  <p className="text-xs text-white-300">
                    {solicitationKindTranslation[solicitation.kind] || solicitation.kind}

                    &nbsp;

                    •

                    &nbsp;

                    {objectivesLabel}
                    {remainingObjectivesCount > 0 && ` e mais ${remainingObjectivesCount}`}
                  </p>

                  <p className="font-light italic text-xs text-white-800 pt-3">
                      Descrição da solicitação
                    </p>

                    <p className="text-sm">
                      {solicitation.observations || '--'}
                    </p>
                </div>
              </div>
            )}
            body={(
              <div className="flex flex-col gap-3">
                <div className="flex gap-3">
                  <div className="w-48">
                    <ImageCarousel
                      files={solicitation.photos?.map((photo, i) => ({ id: `${solicitation}_${i}`, file: photo.url })) || []}
                      className="size-48 border-none"
                      showRemoveButton={false}
                      Placeholder={(
                        <ImageWithPlaceholder
                          placeholderIcon={CameraOffIcon}
                          className="size-48 rounded-lg bg-white-800"
                          iconClassName="size-36"
                        />
                      )}
                      showExpandButton
                      showNavigationButtons
                    />
                  </div>

                  <div className="flex-1">
                    {Number(solicitation.objectives?.length) > 0 ? (
                      <div className="flex flex-col gap-2 max-h-[380px] overflow-auto">
                        {solicitation.objectives?.map((objective) => (
                          <div key={objective.id}>
                            <div className="bg-white-100 px-4 py-2 rounded-lg flex items-center gap-2">
                              <MaintenanceObjective
                                systemDisplayName={objective.system.display_name}
                                actionDisplayName={objective.action.display_name}
                                partDisplayName={objective.part.display_name}
                                actionDescription={objective.action.description}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyDataMessage
                        className="h-full py-0"
                        message="Essa solicitação não possui objetos de manutenção"
                      />
                    )}
                  </div>
                </div>

                <div className="flex gap-8">
                  <ResumeItem
                    label="Autor"
                    value={solicitation.creator?.full_name || '--'}
                    labelClassName="text-xs"
                    valueClassName="text-sm"
                  />

                  <ResumeItem
                    label="Data e hora da abertura"
                    value={formatDateTime(new Date(solicitation.created_at))}
                    labelClassName="text-xs"
                    valueClassName="text-sm"
                  />
                </div>
              </div>
            )}
          />
          );
        })}
      </div>

      {!solicitationsIsPending ? (
        <div
          className={twMerge(
            'w-full px-4 py-2 bg-white-200 rounded-b-lg flex justify-between items-center',
            typeof solicitationsMeta.total === 'undefined' && 'justify-end',
          )}
        >
          {typeof solicitationsMeta.total !== 'undefined' ? (
            <p className="text-xs italic font-light">
              Mostrando {solicitations.length} resultados de {solicitationsMeta.total}
            </p>
          ) : null}
          {setPage && (
            <Pagination
              currentPage={page || 1}
              totalPages={solicitationsMeta.total_pages}
              setPage={setPage}
            />
          )}
        </div>
      ) : (
        <div className="h-8 w-full px-4 bg-white-200 rounded-b-lg flex justify-between items-center">
          <Skeleton className="h-4 w-40" />
        </div>
      )}
    </div>
  );
}

export default MaintenanceWorkflowSelectSolicitations;
